import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';

import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import {
  hasConfiguredEvents,
  hasConfiguredFb,
  hasConfiguredIntegrations,
  selectXRayConfig,
} from '@mm/state/selectors/xrayConfigSelectors';
import { useSelector } from 'react-redux';
import { EventsStep } from './EventsStep/EventsStep';
import { FacebookSettingsStep } from './FacebookSettingsStep/FacebookSettingsStep';
import { GoLiveStep } from './GoLiveStep/GoLiveStep';
import css from './SetupForm.module.css';
import { useViewActions } from '@mm/state/actions/viewActions';
import { Menu } from '../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import CollapsibleContentContainer from '../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';

type SetupFormProps = {
  className?: string;
};

const steps = {
  events: {
    path: '',
    number: '1',
    title: 'Events',
    description: 'Select what events you want to send to Facebook',
  },
  fbSettings: {
    path: '/facebook-settings',
    title: 'Facebook Settings',
    description:
      'In order to send event data to Facebook, we need the following information from your Facebook account.',
    number: '2',
  },
  goLive: {
    path: '/start',
    title: 'Go Live',
    description:
      'We recommend testing your configuration before getting started, but you can continue without testing.',
    number: '3',
  },
};

export const emptyEventsConfig = {
  viewContent: '',
  viewItem: '',
  search: '',
  addToCart: '',
  initiateCheckout: '',
  addPaymentInfo: '',
  purchase: '',
  subscriptionPurchase: '',
  completeRegistration: '',
  subscribe: '',
};

export const facebookConversionApiEvents = [
  {
    key: 'viewContent',
    title: 'View Content',
    info: 'Standard Page view events should just include your website URL. Enter your website URL.',
    placeholder: 'Add URL',
  },
  {
    key: 'viewItem',
    title: 'View Item',
    info: (
      <>
        Enter the URL for a product view. Typically this is{' '}
        <strong>yourwebsite.com/product</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  {
    key: 'addToCart',
    title: 'Add to Cart',
    info: (
      <>
        Enter the URL for a cart view. Typically this is{' '}
        <strong>yourwebsite.com/cart</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  {
    key: 'initiateCheckout',
    title: 'Initiate Checkout',
    info: (
      <>
        Enter the URL for a checkout page. Typically this is{' '}
        <strong>yourwebsite.com/checkout</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  // {
  //   key: 'addPaymentInfo',
  //   title: 'Add Payment Info',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
  {
    key: 'purchase',
    title: 'Purchase',
    info: (
      <>
        Enter the URL for a completed purchase. Typically this is{' '}
        <strong>yourwebsite.com/thankyou</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  // {
  //   key: 'subscriptionPurchase',
  //   title: 'Subscription Purchase',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
  {
    key: 'search',
    title: 'Search',
    info: 'Enter the URL for a search result.',
    placeholder: 'Add URL',
  },
  {
    key: 'completeRegistration',
    title: 'Complete Registration',
    info: 'Enter the URL for a account sign up or account creation page.',
    placeholder: 'Add URL',
  },
  {
    key: 'activeOnSite',
    title: 'Active on Site',
  },
  // {
  //   key: 'subscribe',
  //   title: 'Subscribe',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
];

export const facebookConversionApiVisibleEventsIds =
  facebookConversionApiEvents.map(v => v.key);

export const SetupForm = ({ className }: SetupFormProps) => {
  const [configReady, setConfigReady] = useState(false);

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const defaultConfiguration = useSelector(selectXRayConfig);

  const containerClassName = classNames(className, css.container);
  const openSections = { 1: true };
  const [isExpanded, toggleIsExpanded] =
    useState<Record<number, boolean>>(openSections);

  const { hideSlideoutMenu } = useViewActions();

  const closeSlideout = async () => {
    hideSlideoutMenu();
  };

  const isDisabled = useMemo(
    () => ({
      fbSettings:
        !(
          hasConfiguredEvents(defaultConfiguration) ||
          hasConfiguredIntegrations(defaultConfiguration)
        ) && !hasConfiguredFb(defaultConfiguration),

      goLive:
        !(
          hasConfiguredEvents(defaultConfiguration) ||
          hasConfiguredIntegrations(defaultConfiguration)
        ) && !hasConfiguredFb(defaultConfiguration),
    }),
    [defaultConfiguration],
  );

  const onReset = () => {
    const shouldReset = window?.confirm?.(
      'All events and Facebook settings will be reset. Are you sure?',
    );

    if (!shouldReset) {
      return;
    }

    try {
      xRayConfigurationCRUDActions.updateXRayEventsConfiguration(chatbotId, {
        facebookPageId,
        configurations: emptyEventsConfig,
        pixelId: '',
        accessToken: '',
        active: false,
      });
      // eslint-disable-next-line no-empty
    } finally {
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await xRayConfigurationCRUDActions.getXRayEventsConfiguration({
          botId: chatbotId,
        });
      } finally {
        setConfigReady(true);
      }
    })();
  }, [chatbotId]);

  return (
    <div className={containerClassName}>
      <div className={css.header}>
        <Button
          css={{ container: css.backBtn }}
          color="transparent"
          onClick={closeSlideout}
        >
          <ArrowLeftOutlined />
          Back to Facebook
        </Button>
        <GoLiveStep isDisabled={isDisabled.goLive} />
        <Menu
          className={css.menu}
          menu={[
            {
              children: <div className={classNames(css.menuAction)}>Reset</div>,
              onClick: onReset,
            },
          ]}
          direction="bottom"
          useThreeDotButton
        />
      </div>

      <CollapsibleContentContainerWithoutLocalState
        orderNumber="1"
        containerName="Events"
        stepperStylingPlatform="sold"
        useCircleNumberStyle
        expandedOnMount
        hideTitleBorder
        className={css.collapse}
        isExpanded={isExpanded[1]}
        toggleIsExpanded={isExpanded => {
          toggleIsExpanded({
            1: isExpanded,
          });
        }}
      >
        <EventsStep
          configReady={configReady}
          onSave={mode => {
            if (mode === 'manual') {
              toggleIsExpanded({
                2: !openSections[2],
              });
            }
          }}
        />
      </CollapsibleContentContainerWithoutLocalState>

      <CollapsibleContentContainerWithoutLocalState
        orderNumber="2"
        containerName="Facebook Settings"
        stepperStylingPlatform="sold"
        useCircleNumberStyle
        expandedOnMount
        hideTitleBorder
        className={css.collapse}
        isExpanded={isExpanded[2]}
        toggleIsExpanded={isExpanded => {
          toggleIsExpanded({
            2: isExpanded,
          });
        }}
      >
        <FacebookSettingsStep
          configReady={configReady}
          isDisabled={isDisabled.fbSettings}
        />
      </CollapsibleContentContainerWithoutLocalState>
    </div>
  );
};
